import { useMemo } from "react";

/**
 * Extract index name from symbol
 * @param {string} symbol - e.g., "NSE:BANKNIFTY25NOV58000PE"
 * @returns {string|null} - e.g., "BANKNIFTY" or null for non-index symbols
 */
function getIndexFromSymbol(symbol) {
  try {
    const symbolUpper = symbol.toUpperCase();

    // Exclude FUT symbols (especially for Fyers which uses NSE: prefix but ends in FUT)
    if (symbolUpper.endsWith("FUT")) return null;

    if (symbolUpper.includes("BSE:SENSEX")) return "SENSEX";
    if (symbolUpper.includes("NSE:BANKNIFTY")) return "BANKNIFTY";
    if (symbolUpper.includes("NSE:MIDCPNIFTY")) return "MIDCPNIFTY";
    if (symbolUpper.includes("NSE:FINNIFTY")) return "FINNIFTY";
    if (symbolUpper.includes("NSE:NIFTY") && !symbolUpper.includes("BANKNIFTY") && !symbolUpper.includes("FINNIFTY") && !symbolUpper.includes("MIDCPNIFTY")) return "NIFTY";

    // Not an index or doesn't match the strict prefixes - return null
    return null;
  } catch (e) {
    console.error("Error extracting index from symbol:", symbol, e);
    return null;
  }
}

/**
 * Normalize order/position side to 'buy' | 'sell' | null
 * Accepts numeric (1,2,-1), strings ('1','2','-1','BUY','SELL') and returns 'buy' or 'sell'
 */
function normalizeSide(rawSide) {
  if (rawSide === null || rawSide === undefined) return null;
  const s = String(rawSide).trim().toLowerCase();
  if (s === "1" || s === "buy" || s === "b") return "buy";
  if (s === "2" || s === "-1" || s === "sell" || s === "s") return "sell";
  return null;
}

/**
 * Calculate MODE (most frequent value) from an array of numbers
 * @param {number[]} numbers
 * @returns {number}
 */
function calculateMode(numbers) {
  if (!numbers || numbers.length === 0) return 0;

  const frequency = {};
  let maxFreq = 0;
  let mode = numbers[0];

  numbers.forEach((num) => {
    frequency[num] = (frequency[num] || 0) + 1;
    if (frequency[num] > maxFreq) {
      maxFreq = frequency[num];
      mode = num;
    }
  });

  return mode;
}

/**
 * Helper: Get multiplier from userMappings for a given client and index
 */
function getMultiplier(userMappings, clientId, index) {
  if (!userMappings || !clientId || !index) return 1; // Default to 1 to avoid division by zero/null
  const mapping = userMappings.find((u) => u.client_id === clientId);
  if (!mapping) return 1;

  let multiplierKey;
  switch (index) {
    case "NIFTY":
      multiplierKey = "multiplier";
      break;
    case "BANKNIFTY":
      multiplierKey = "banknifty_multiplier";
      break;
    case "FINNIFTY":
      multiplierKey = "finnifty_multiplier";
      break;
    case "MIDCPNIFTY":
      multiplierKey = "midcpnifty_multiplier";
      break;
    case "SENSEX":
      multiplierKey = "sensex_multiplier";
      break;
    default:
      multiplierKey = `${index.toLowerCase()}_multiplier`;
  }

  const val = Number(mapping[multiplierKey]);
  return val > 0 ? val : 1; // Return 1 if multiplier is 0 or invalid to act as fallback
}

/**
 * Calculate base quantity for each unique symbol+strategy combination
 * NOW NORMALIZED by client multipliers to find the true "1x" base qty
 * @param {Array} positions - Position data
 * @param {Array} userMappings - User mappings to lookup multipliers
 * @returns {Object} - { "symbol_strategy": baseQty, ... }
 */
function calculateSymbolStrategyBaseQty(positions, userMappings) {
  const symbolStrategyQtyMap = {};

  positions.forEach((position) => {
    const index = getIndexFromSymbol(position.symbol);

    // Skip non-index positions
    if (!index) return;

    // Skip closed positions (net_qty = 0)
    const netQty = position.net_qty || position.qty || 0;
    if (Math.abs(netQty) === 0) {
      return;
    }

    const key = `${position.symbol}_${position.strategy || "NO_STRATEGY"}`;

    if (!symbolStrategyQtyMap[key]) {
      symbolStrategyQtyMap[key] = {
        symbol: position.symbol,
        strategy: position.strategy,
        index,
        normalizedQuantities: [], // Store normalized quantities
      };
    }


    const clientId = position.client_id;
    const qty = Math.abs(netQty);
    const multiplier = getMultiplier(userMappings, clientId, index);
    const normalizedQty = qty / multiplier;

    symbolStrategyQtyMap[key].normalizedQuantities.push(normalizedQty);
  });

  // Calculate MODE of NORMALIZED quantities
  const baseQtyMap = {};
  Object.entries(symbolStrategyQtyMap).forEach(([key, data]) => {
    const mode = calculateMode(data.normalizedQuantities);
    baseQtyMap[key] = {
      symbol: data.symbol,
      strategy: data.strategy,
      index: data.index,
      baseQty: mode, // This is now the 1x base qty
      clients: data.normalizedQuantities.length,
      allQtys: data.normalizedQuantities,
    };
  });

  // console.log("📊 Symbol+Strategy Base Qty Map (Normalized):", baseQtyMap);
  return baseQtyMap;
}

/**
 * Group and sum quantities by client, index, and side
 * @param {Array} positions - Position data
 * @returns {Object} - Grouped data
 */
function groupPositionsByClientIndexSide(positions) {
  const grouped = {};

  positions.forEach((position) => {
    const index = getIndexFromSymbol(position.symbol);

    // Skip non-index positions (stocks, MCX, etc.)
    if (!index) {
      return;
    }

    // Positions don't have status - they represent actual holdings
    // If a position has net_qty of 0, it means it's closed and should be filtered out
    const netQty = position.net_qty || position.qty || 0;
    if (Math.abs(netQty) === 0) {
      // console.log(`🚫 Skipping closed position (qty=0) for ${position.symbol}`);
      return;
    }

    const key = `${position.client_id}_${index}_${position.side}`;

    if (!grouped[key]) {
      grouped[key] = {
        client_id: position.client_id,
        index,
        side: position.side,
        qty: 0,
        pnl: 0,
        positions: [],
      };
    }

    // Use net_qty from WebSocket data, fallback to qty
    const qty = Math.abs(netQty);
    // Use pl from WebSocket data, fallback to pnl
    const pnl = position.pl || position.pnl || 0;

    grouped[key].qty += qty;
    grouped[key].pnl += pnl;
    grouped[key].positions.push(position);
  });

  return grouped;
}

/**
 * Group and sum sell orders by client, index, and side
 * @param {Array} orders - Orders data
 * @returns {Object} - Grouped sell orders
 */
function groupSellOrdersByClientIndexSide(orders) {
  const grouped = {};

  // Filter only sell orders (tolerant normalization)
  const sellOrders = orders.filter(
    (order) => normalizeSide(order.side) === "sell"
  );

  // console.log(
  //   `🔍 [DEBUG] Filtered ${sellOrders.length} sell orders from ${orders.length} total orders`
  // );

  sellOrders.forEach((order) => {
    const index = getIndexFromSymbol(order.symbol);
    // Handle both clientId (camelCase) and client_id (snake_case)
    const clientId = order.clientId || order.client_id;
    const normalized = normalizeSide(order.side) || String(order.side);
    const key = `${clientId}_${index}_${normalized}`;

    if (!grouped[key]) {
      grouped[key] = {
        client_id: clientId,
        index,
        side: normalized,
        qty: 0,
      };
    }

    grouped[key].qty += Math.abs(order.qty || 0);
  });

  return grouped;
}

/**
 * Determine color based on validation rules
 * Position View: Only check if Position QTY matches SL QTY
 * (No base qty comparison - that's for Order View only)
 * @param {number} positionQty
 * @param {number} slQty
 * @returns {string} - 'green' or 'red'
 */
function determineColor(positionQty, slQty) {
  // Check if Position qty matches SL qty
  if (positionQty !== slQty) {
    return "red";
  }

  // Condition passes (GREEN)
  return "green";
}

/**
 * Hook for Position View data processing
 * @param {Array} positions - Position data from WebSocket
 * @param {Array} orders - Orders data from API
 * @param {Array} userMappings - User mapping data with multipliers
 * @param {number} positionsUpdateVersion - Version counter to force re-computation
 * @returns {Object} - { data, baseQtyMap, activeIndexes }
 */
export function usePositionsView(
  positions,
  orders,
  userMappings,
  positionsUpdateVersion
) {
  const positionViewData = useMemo(() => {
    // If no positions, return empty
    if (!positions || positions.length === 0) {
      // console.log("⚠️ No positions data");
      return { data: [], baseQtyMap: {}, activeIndexes: [] };
    }

    // console.log("🔄 Processing positions view:", {
    //   positionsCount: positions.length,
    //   ordersCount: orders?.length || 0,
    //   userMappingsCount: userMappings?.length || 0,
    //   version: positionsUpdateVersion,
    // });

    // Log sample data for debugging
    if (positions.length > 0) {
      // console.log("📦 Sample position:", positions[0]);
    }
    if (userMappings && userMappings.length > 0) {
      // console.log("👤 Sample user mapping:", userMappings[0]);
      // console.log(
      //   "👥 All client_ids in user mappings:",
      //   userMappings.map((u) => u.client_id)
      // );
    }

    // Step 1: Group positions by client, index, and side
    const groupedPositions = groupPositionsByClientIndexSide(positions);
    // console.log("📊 Grouped positions:", groupedPositions);

    // Step 1.5: Calculate symbol+strategy base qty for PositionComparisonModal
    const symbolStrategyBaseQty = calculateSymbolStrategyBaseQty(positions, userMappings);

    // Step 2: Group sell orders by client, index, and side (if orders available)
    const groupedSellOrders = orders
      ? groupSellOrdersByClientIndexSide(orders)
      : {};
    if (orders) {
      // console.log("🛒 Grouped sell orders:", groupedSellOrders);
      // console.log("📋 Sample order:", orders[0]);
    } else {
      console.log("⏳ Orders not loaded yet, SL QTY will be 0");
    }

    // Step 3: Create client-wise index data
    const clientIndexMap = {};

    Object.values(groupedPositions).forEach((posGroup) => {
      const clientId = posGroup.client_id;
      const index = posGroup.index;

      if (!clientIndexMap[clientId]) {
        clientIndexMap[clientId] = {};
      }

      if (!clientIndexMap[clientId][index]) {
        clientIndexMap[clientId][index] = {
          qty: 0,
          slQty: 0,
          pnl: 0,
          positions: [],
        };
      }

      // Aggregate position data
      clientIndexMap[clientId][index].qty += posGroup.qty;
      clientIndexMap[clientId][index].pnl += posGroup.pnl;
      clientIndexMap[clientId][index].positions = (
        clientIndexMap[clientId][index].positions || []
      ).concat(posGroup.positions || []);

      // Find matching sell order qty (use normalized side key)
      const sellOrderKey = `${clientId}_${index}_sell`;
      if (groupedSellOrders[sellOrderKey]) {
        clientIndexMap[clientId][index].slQty =
          groupedSellOrders[sellOrderKey].qty;
        // console.log(
        //   `✅ Matched SL for ${clientId} ${index}:`,
        //   groupedSellOrders[sellOrderKey].qty
        // );
      } else {
        //console.log(`❌ No SL match for key: ${sellOrderKey}`);
      }
    });

    // Step 4: Add Orphans from Sell Orders (Orders without Positions)
    Object.values(groupedSellOrders).forEach(orderGroup => {
      const { client_id, index, qty } = orderGroup;
      // Skip null index (filtered out NFO/BFO)
      if (!index) return;

      if (!clientIndexMap[client_id]) {
        clientIndexMap[client_id] = {};
      }

      // If index entry doesn't exist, create it (it's an orphan)
      if (!clientIndexMap[client_id][index]) {
        clientIndexMap[client_id][index] = {
          qty: 0,
          slQty: qty, // Set the SL Qty
          pnl: 0
        };
      }
      // If it exists, slQty was already handled in Step 3
    });

    // console.log("🗂️ Client Index Map:", clientIndexMap);

    // Step 5: Build final position view data with color coding
    const data = [];

    Object.keys(clientIndexMap).forEach((clientId) => {
      // Prioritize Dhan XTS mapping if multiple exist for the same client_id
      let userMapping = null;
      if (userMappings) {
        const matches = userMappings.filter((u) => u.client_id === clientId);
        // Find XTS specifically, or fallback to the first one found (e.g. SS)
        userMapping = matches.find(u => (u.broker || "").toLowerCase() === "dhan_xts") || matches[0];
      }

      if (!userMapping) {
        if (userMappings && userMappings.length > 0) {
          // console.warn(`⚠️ No user mapping found for client_id: "${clientId}"`);
          // console.log(
          //   "Available user mappings:",
          //   userMappings.map((u) => u.client_id)
          // );
        }
        // Still show the position with placeholder name
      }

      const rowData = {
        clientId,
        clientName: userMapping ? userMapping.name : `Client ${clientId}`,
        status: userMapping ? userMapping.status : "unknown",
        broker: userMapping ? userMapping.broker : "unknown",
      };

      // Add index-wise data (indexes only, no stocks)
      const indexes = [
        "NIFTY",
        "BANKNIFTY",
        "FINNIFTY",
        "MIDCPNIFTY",
        "SENSEX",
      ];

      indexes.forEach((index) => {
        // Only show index data if the client actually has positions in that index
        if (clientIndexMap[clientId][index]) {
          // Map index names to multiplier field names in user mapping
          let multiplierKey;
          switch (index) {
            case "NIFTY":
              multiplierKey = "multiplier"; // Updated to use 'multiplier' field
              break;
            case "BANKNIFTY":
              multiplierKey = "banknifty_multiplier";
              break;
            case "FINNIFTY":
              multiplierKey = "finnifty_multiplier";
              break;
            case "MIDCPNIFTY":
              multiplierKey = "midcpnifty_multiplier";
              break;
            case "SENSEX":
              multiplierKey = "sensex_multiplier";
              break;
            default:
              multiplierKey = `${index.toLowerCase()}_multiplier`;
          }

          const multiplier = userMapping ? userMapping[multiplierKey] || 0 : 0;
          const indexData = clientIndexMap[clientId][index];

          const color = determineColor(indexData.qty, indexData.slQty);

          rowData[index.toLowerCase()] = {
            multiplier,
            qty: indexData.qty,
            slQty: indexData.slQty,
            pnl: indexData.pnl,
            positions: indexData.positions || [], // Include raw positions for LiveAggregatedPNLCell
            color,
            expectedQty: 0, // Not used in position view
          };
        }
      });

      data.push(rowData);
    });

    // Determine active indexes
    // Filter to only show indexes that have data (remove stock)
    const indexes = ["nifty", "banknifty", "finnifty", "midcpnifty", "sensex"];
    const activeIndexes = indexes.filter((index) =>
      data.some((row) => row[index] !== undefined)
    );

    // Calculate base qty for each index (MODE of NORMALIZED all quantities)
    const baseQtyMap = {};
    activeIndexes.forEach((index) => {
      const allNormalizedQtys = data
        .map((row) => {
          const rawQty = row[index]?.qty;
          const multiplier = row[index]?.multiplier || 1;
          // normalize
          return rawQty ? rawQty / (multiplier > 0 ? multiplier : 1) : undefined;
        })
        .filter((qty) => qty !== undefined && qty > 0);

      if (allNormalizedQtys.length > 0) {
        baseQtyMap[index] = calculateMode(allNormalizedQtys);
      }
    });

    return { data, baseQtyMap, activeIndexes, symbolStrategyBaseQty };
  }, [positions, orders, userMappings]); // REMOVED positionsUpdateVersion to stop re-render on LTP

  return positionViewData;
}
